import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box, Container, Typography, Grid, Chip, Stack, Button,
  CircularProgress, Divider, Breadcrumbs, Link as MuiLink,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Clock, IndianRupee, BarChart3, Cloud, Network,
  Database, Target, ShieldCheck, BookOpen, CheckCircle2,
} from "lucide-react";
import { getLearningPath } from "../api/learningPaths";
import api from "../api/axios";
import PageShell from "../components/layout/PageShell";
import ContactSection from "../components/shared/ContactSection";

/* ------------------------------------------------------------------ */
/*  card_image_url comes back as a relative path (e.g.                */
/*  "/media/learning-paths/cards/xyz.png"). Rendered directly, that   */
/*  resolves against the React dev server's own origin instead of     */
/*  the API server, so it 404s. Resolve it against the API's actual   */
/*  origin instead — same pattern as CourseDetailPage.                */
/* ------------------------------------------------------------------ */
const API_ORIGIN = new URL(api.defaults.baseURL, window.location.origin).origin;

function resolveMediaUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path; // already absolute
  return `${API_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
}

const iconMap = {
  cloud: Cloud,
  security: ShieldCheck,
  network: Network,
  system: Database,
  data: BarChart3,
  seo: BarChart3,
  marketing: Target,
  project: Target,
};

function getIconKey(title = "") {
  return Object.keys(iconMap).find((k) => title.toLowerCase().includes(k)) || "default";
}

const LEVEL_COLOR = {
  BEGINNER: { bg: "#ECFDF5", color: "#047857" },
  INTERMEDIATE: { bg: "#FFFBEB", color: "#B45309" },
  ADVANCED: { bg: "#FEF2F2", color: "#B91C1C" },
};

function formatSalary(min, max) {
  if (min == null && max == null) return null;
  const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : n);
  if (min != null && max != null) return `₹${fmt(min)} - ₹${fmt(max)}`;
  return `₹${fmt(min ?? max)}`;
}

function parseBadges(highlightBadges) {
  if (!highlightBadges) return [];
  return String(highlightBadges)
    .split(",")
    .map((b) => b.trim())
    .filter(Boolean);
}

// Hero image with graceful fallback to an icon plate if there's no
// image or the resolved URL fails to load — same pattern as CourseDetailPage.
function PathHeroImage({ src, alt, iconKey }) {
  const [failed, setFailed] = useState(!src);
  const Icon = iconMap[iconKey] || BookOpen;
  return (
    <Box
      sx={{
        width: "100%", height: { xs: 220, md: 320 }, borderRadius: "16px", overflow: "hidden",
        bgcolor: "#F1F5FB", display: "flex", alignItems: "center", justifyContent: "center",
        border: "1px solid rgba(15,23,42,0.08)",
      }}
    >
      {!failed && src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          onError={() => setFailed(true)}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Icon size={56} color="#94A3B8" strokeWidth={1.5} />
      )}
    </Box>
  );
}

export default function LearningPathDetailPage() {
  const { slug } = useParams();

  const { data: path, isLoading, isError } = useQuery({
    queryKey: ["learning-path", slug],
    queryFn: () => getLearningPath(slug).then((r) => r.data),
    enabled: !!slug,
  });

  const iconKey = getIconKey(path?.title);
  const levelStyle = LEVEL_COLOR[path?.level] ?? { bg: "#F1F5F9", color: "#334155" };
  const salaryLabel = path ? formatSalary(path.salary_min, path.salary_max) : null;
  const badges = path ? parseBadges(path.highlight_badges) : [];
  const blurb = path?.short_tagline || path?.description;

  return (
    <PageShell>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
        <Breadcrumbs sx={{ fontSize: 13, mb: 3 }}>
          <MuiLink component={Link} to="/" sx={{ textDecoration: "none", color: "#64748B", fontSize: 13 }}>
            Home
          </MuiLink>
          <MuiLink component={Link} to="/learn" sx={{ textDecoration: "none", color: "#64748B", fontSize: 13 }}>
            Learning Paths
          </MuiLink>
          <Typography sx={{ fontSize: 13, color: "#0B1530", fontWeight: 600 }}>
            {path?.title || "…"}
          </Typography>
        </Breadcrumbs>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress />
          </Box>
        ) : isError || !path ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.3rem", color: "#0B1530", mb: 2 }}>
              Learning path not found
            </Typography>
            <MuiLink
              component={Link}
              to="/learn"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 13.5, fontWeight: 700, color: "#2563EB", textDecoration: "none" }}
            >
              <ArrowLeft size={15} /> Back to learning paths
            </MuiLink>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 4, lg: 6 }}>
            {/* Main column */}
            <Grid item xs={12} lg={8}>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                {path.level && (
                  <Chip
                    label={path.level.charAt(0) + path.level.slice(1).toLowerCase()}
                    sx={{ fontWeight: 700, bgcolor: levelStyle.bg, color: levelStyle.color }}
                  />
                )}
                {path.placement_support && (
                  <Chip
                    label="Placement support"
                    sx={{ bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", fontWeight: 700 }}
                  />
                )}
                {badges.map((b) => (
                  <Chip key={b} label={b} variant="outlined" sx={{ fontWeight: 700 }} />
                ))}
              </Stack>

              <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.6rem", md: "2rem" }, color: "#0B1530", mb: 1.5 }}>
                {path.title}
              </Typography>

              {blurb && (
                <Typography sx={{ fontSize: 15.5, color: "#475569", lineHeight: 1.7, mb: 3 }}>
                  {blurb}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 4 }}>
                {path.duration_weeks && (
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Clock size={16} color="#64748B" />
                    <Typography fontSize={13.5} color="#64748B" fontWeight={600}>
                      {path.duration_weeks} weeks
                    </Typography>
                  </Stack>
                )}
                {salaryLabel && (
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <IndianRupee size={16} color="#64748B" />
                    <Typography fontSize={13.5} color="#64748B" fontWeight={600}>
                      {salaryLabel}
                    </Typography>
                  </Stack>
                )}
              </Box>

              <PathHeroImage src={resolveMediaUrl(path.card_image_url)} alt={path.title} iconKey={iconKey} />

              {path.description && (
                <Box sx={{ mt: 5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0B1530", mb: 1 }}>
                    About this path
                  </Typography>
                  <Divider sx={{ mb: 2.5 }} />
                  <Typography sx={{ fontSize: 14.5, color: "#334155", lineHeight: 1.85, whiteSpace: "pre-line" , textAlign: "justify"}}>
                    {path.description}
                  </Typography>
                </Box>
              )}

              {Array.isArray(path.courses) && path.courses.length > 0 && (
                <Box sx={{ mt: 5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0B1530", mb: 1 }}>
                    Courses included
                  </Typography>
                  <Divider sx={{ mb: 2.5 }} />
                  <Stack spacing={1.5}>
                    {path.courses.map((c) => (
                      <Stack
                        key={c.id ?? c.slug}
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                        component={c.slug ? Link : "div"}
                        to={c.slug ? `/courses/${c.slug}` : undefined}
                        sx={{ textDecoration: "none", color: "inherit" }}
                      >
                        <CheckCircle2 size={17} color="#059669" />
                        <Typography fontSize={14} fontWeight={600} color="#0B1530">
                          {c.title}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              <MuiLink
                component={Link}
                to="/learn"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, mt: 5, fontSize: 13.5, fontWeight: 700, color: "#2563EB", textDecoration: "none" }}
              >
                <ArrowLeft size={15} /> Back to learning paths
              </MuiLink>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ position: "sticky", top: 100 }}>
                <Box
                  sx={{
                    bgcolor: "white", borderRadius: "16px", p: 3, mb: 2.5,
                    border: "1px solid rgba(15,23,42,0.08)",
                    boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
                  }}
                >
                  <Button
                    variant="contained" fullWidth size="large"
                    component="a" href="#contact"
                    sx={{
                      borderRadius: "10px", mb: 1.5, textTransform: "none", fontWeight: 700,
                      bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" },
                    }}
                  >
                    Enroll Now
                  </Button>
                  <Button
                    variant="outlined" fullWidth size="large"
                    component="a" href="#contact"
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, borderColor: "rgba(15,23,42,0.15)", color: "#334155" }}
                  >
                    Request Brochure
                  </Button>
                </Box>

                <Box
                  sx={{
                    bgcolor: "#F8FAFC", borderRadius: "16px", p: 3,
                    border: "1px solid rgba(15,23,42,0.06)",
                  }}
                >
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.75 }}>
                    Path Details
                  </Typography>
                  {[
                    ["Level", path.level ? path.level.charAt(0) + path.level.slice(1).toLowerCase() : null],
                    ["Duration", path.duration_weeks ? `${path.duration_weeks} weeks` : null],
                    ["Salary range", salaryLabel],
                    ["Placement support", path.placement_support ? "Yes" : null],
                    ["Status", path.status],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <Box
                      key={label}
                      sx={{ display: "flex", justifyContent: "space-between", py: 1.1, borderBottom: "1px solid rgba(15,23,42,0.06)" }}
                    >
                      <Typography fontSize={13} color="#64748B">{label}</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#0B1530">{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>

      <ContactSection />
    </PageShell>
  );
}